import React, { useState, useEffect } from 'react';
import {
  Accordion,
  Button,
  Group,
  NavLink,
  Text,
  MantineProvider,
  createTheme,
  Tabs,
  Card,
  Space,
  Container,
  Drawer,
  ActionIcon,
  Burger,
  Box,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Link } from '@inertiajs/react';
import {
  IconPlus,
  IconSearch,
  IconMapPin,
  IconWorld,
  IconMenu2,
  IconUserFilled,
  IconSettings,
  IconNotebook,
  IconWallet,
  IconSchool,
  IconBriefcase,
  IconBookmarks,
  IconCalendarStats,
  IconList,
  IconUsers,
  IconReceiptDollar,
  IconReceipt,
  IconLogin2,
  IconUserPlus,
} from '@tabler/icons-react';
import PrivateLessonsManagement from '../../components/PrivateLessons/PrivateLessonsManagement';
import UserAccount from '../../components/UserAccount';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../../styles/UserSettings.css';

// Define your custom theme
const theme = createTheme({
  primaryColor: 'orange',
  colors: {
    orange: [
      '#FEF5ED',
      '#FDE3D1',
      '#FCB693',
      '#FA9D73',
      '#F87F4D',
      '#F66A33',
      '#EA580C',
      '#C44B0A',
      '#9E3D08',
      '#783006',
    ],
  },
  fontFamily: 'Poppins, sans-serif',
  headings: {
    fontFamily: 'Poppins, sans-serif',
  },
  globalStyles: (theme) => ({
    body: { color: '#f2f2f2' },
    '.sidebar-content': { color: '#f2f2f2' },
  }),
});

interface UserSettingsProps {
  user: {
    id: number;
    username: string;
    bio: string;
    email: string;
    name: string;
    superadmin?: boolean;
    hasOrganizer?: boolean;
    hasArtist?: boolean;
    artists?: Array<{ id: number; name: string; isPortalEnabled: boolean }>;
    claimed_artist?: {
      id: number;
      name: string;
      slug: string;
      private_lessons_enabled: boolean;
    };
  };
  available_roles?: Array<{
    id: number;
    name: string;
  }>;
  pendingCounter?: number; // Explicitly typed as number
  deletedCounter?: number;
  subscriptions_by_type?: any;
  private_lesson_types?: Array<{
    id: number;
    name: string;
    location: string;
    duration: number;
    price: number;
    payment_required: boolean;
    color: string;
  }>;
  private_lesson_pages?: Array<{
    id: number;
    description: string;
    additional_info: string;
    timezone: string;
    slug: string;
    created_at: string;
    private_lesson_type: {
      id: number;
      name: string;
      location: string;
      duration: number;
      price: number;
      color: string;
    };
  }>;
  private_lesson_bookings?: Array<{
    id: number;
    user_id: number;
    private_lesson_page_id: number;
    start_time: string;
    end_time: string;
    status: string;
  }>;
}

/* RightContentSection Component */
interface RightContentSectionProps {
  section: string;
  user: UserSettingsProps['user'];
  claimedArtist?: UserSettingsProps['user']['claimed_artist'];
  privateLessonTypes?: Array<{
    id: number;
    name: string;
    location: string;
    duration: number;
    price: number;
    payment_required: boolean;
    color: string;
  }>;
  privateLessonPages?: Array<{
    id: number;
    description: string;
    additional_info: string;
    timezone: string;
    slug: string;
    created_at: string;
    private_lesson_type: {
      id: number;
      name: string;
      location: string;
      duration: number;
      price: number;
      color: string;
    };
  }>;
  privateLessonBookings?: Array<{
    id: number;
    user_id: number;
    private_lesson_page_id: number;
    start_time: string;
    end_time: string;
    status: string;
  }>;
  initialTab?: string | null;
  available_roles?: Array<{ value: string; label: string }>;
}

const RightContentSection: React.FC<RightContentSectionProps> = ({
  section,
  user,
  claimedArtist,
  privateLessonTypes,
  privateLessonPages,
  privateLessonBookings,
  initialTab,
  available_roles,
}) => {
  // Add detailed logging
  console.log('RightContentSection props:', {
    section,
    claimedArtist,
    privateLessonTypes: privateLessonTypes?.length,
    privateLessonPages: privateLessonPages?.length,
    privateLessonBookings: privateLessonBookings?.length,
    initialTab,
  });
  console.log('privateLessonPages detail:', privateLessonPages);

  if (section === 'settings') {
    return (
      <Container style={{ padding: '1rem' }}>
        <Text size="xl" fw={700} mb="1rem">
          Your Settings
        </Text>
        {/* Tab Navigation */}
        <Tabs defaultValue="profile">
          <Tabs.List>
            <Tabs.Tab value="profile">Profile</Tabs.Tab>
            <Tabs.Tab value="payments" disabled>
              Payments
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="xs">
            {/* Profile Tab Content */}
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{
                minHeight: '100px',
                marginBottom: '1rem',
                backgroundColor: '#757575',
                color: '#f2f2f2',
              }}
            >
              <Text fw={700} mb="xs" c="#f2f2f2">
                Your User Account
              </Text>
              <Text mb="md" c="#f2f2f2">
                Update your email address, password, and other personal information.
              </Text>
              <Button component="a" href={`/edit_user/${user.id}`} variant="filled">
                Edit Your User Account
              </Button>
            </Card>
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{ minHeight: '100px', backgroundColor: '#757575', color: '#f2f2f2' }}
            >
              <Text fw={500} mb="xs">
                Your Subscriptions
              </Text>
              <Text mb="md">
                Manage your subscriptions here. You will get an email weekly every Sunday morning with upcoming events.
              </Text>
              <Button component="a" href={`/user_subscriptions/${user.id}`} variant="filled">
                Manage Your Subscriptions
              </Button>
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="payments" pt="xs">
            {/* Payments Tab Content */}
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{
                minHeight: '100px',
                marginBottom: '1rem',
                backgroundColor: '#757575',
                color: '#f2f2f2',
              }}
            >
              <Text fw={500} mb="xs">
                Payment Methods
              </Text>
              <Text mb="md">
                Monetize your following by connecting to Stripe to receive payments from your supporters.
              </Text>
              <Button component="a" href="/connect_stripe" variant="filled">
                Connect to Stripe
              </Button>
            </Card>
            <Card
              shadow="sm"
              padding="lg"
              radius={8}
              style={{ minHeight: '100px', backgroundColor: '#757575', color: '#f2f2f2' }}
            >
              <Text component="div" fw={500} mb="xs">
                Currency
              </Text>
              <select className="form-select" id="currency" defaultValue="USD">
                <option value="USD">US $</option>
                <option value="CAD">CAD $</option>
                <option value="EUR">EUR €</option>
              </select>
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Container>
    );
  } else if (section === 'privateLessons' && claimedArtist) {
    console.log('Rendering PrivateLessonsManagement with:', {
      privateLessonTypes,
      privateLessonPages,
      privateLessonBookings,
      initialTab,
    });

    // When privateLessons section is active, render the PrivateLessonsManagement component
    // Pass the initialTab to the component
    return (
      <PrivateLessonsManagement
        currentUser={user}
        claimedArtist={claimedArtist}
        privateLessonTypes={privateLessonTypes}
        privateLessonPages={privateLessonPages}
        privateLessonBookings={privateLessonBookings}
        available_roles={available_roles?.map((role) => ({ value: role.id.toString(), label: role.name }))}
      />
    );
  } else if (section === 'account') {
    return (
      <Container style={{ padding: '1rem' }}>
        <Text size="xl" fw={700} mb="1rem">
          Manage Your Account
        </Text>
        <UserAccount
          user={user}
          update_url={`/update_profile/${user.username}`}
          available_roles={available_roles}
          onUpdateSuccess={() => {
            // Optional callback for when the update succeeds
            // For example, you could show a notification or refresh data
          }}
        />
      </Container>
    );
  }

  // For other sections or default content
  return (
    <Container style={{ padding: '1rem' }}>
      <Text size="xl" fw={700} mb="1rem">
        Welcome to Your Dashboard
      </Text>
      <Card shadow="sm" padding="lg" radius={8} style={{ backgroundColor: '#757575', color: '#f2f2f2' }}>
        <Text>Select an option from the sidebar to manage your account.</Text>
      </Card>
    </Container>
  );
};

/* Main UserSettings Page */
const UserSettings: React.FC<UserSettingsProps> = ({
  user,
  pendingCounter = 0,
  deletedCounter = 0,
  subscriptions_by_type,
  private_lesson_types,
  private_lesson_pages,
  private_lesson_bookings,
  available_roles,
}) => {
  // Check if we're on mobile
  const isMobile = useMediaQuery('(max-width: 768px)');

  // State for sidebar visibility on mobile
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Add console log to debug props
  console.log('UserSettings Props:', {
    user,
    pendingCounter,
    deletedCounter,
    subscriptions_by_type,
    privateLessonTypes: private_lesson_types?.length,
    privateLessonPages: private_lesson_pages?.length,
    privateLessonBookings: private_lesson_bookings?.length,
  });

  const isSuperadmin = user && user.superadmin;
  const hasOrganizer = user && user.hasOrganizer;
  const hasArtist = user && user.hasArtist;
  const portalArtists = user?.artists?.filter((a) => a.isPortalEnabled) || [];

  // Parse URL query parameters to get the section and tab
  const [activeSection, setActiveSection] = useState<string>(() => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const sectionParam = urlParams.get('section');

    // Then check localStorage (this might be set by the ManageBadge component)
    const storedSection = localStorage.getItem('activeSettingsSection');

    // Return the section from URL params, localStorage, or default to 'default'
    return sectionParam || storedSection || 'default';
  });

  // Initialize tab state for PrivateLessonsManagement
  const [activeTab, setActiveTab] = useState<string | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const storedTab = localStorage.getItem('activePrivateLessonsTab');
    return tabParam || storedTab || null;
  });

  // Setup event listener for tab navigation
  useEffect(() => {
    const handleNavigation = (event: CustomEvent) => {
      const { section, tab } = event.detail;
      if (section) setActiveSection(section);
      if (tab) setActiveTab(tab);
    };

    // Add event listener
    window.addEventListener('navigateToPrivateLessonBookings', handleNavigation as EventListener);

    // Clean up
    return () => {
      window.removeEventListener('navigateToPrivateLessonBookings', handleNavigation as EventListener);

      // Optionally clear localStorage when component unmounts
      localStorage.removeItem('activeSettingsSection');
      localStorage.removeItem('activePrivateLessonsTab');
    };
  }, []);

  // Update URL with current state of active section and tab
  useEffect(() => {
    const url = new URL(window.location.href);
    if (activeSection !== 'default') {
      url.searchParams.set('section', activeSection);
    } else {
      url.searchParams.delete('section');
    }

    if (activeTab) {
      url.searchParams.set('tab', activeTab);
    } else {
      url.searchParams.delete('tab');
    }

    // Update browser history without reloading the page
    window.history.replaceState({}, '', url.toString());
  }, [activeSection, activeTab]);

  // Function to close sidebar when a menu item is clicked (mobile only)
  const handleMenuItemClick = (section) => {
    setActiveSection(section);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Create the sidebar content (used for both desktop and mobile)
  const SidebarContent = ({ user, pendingCounter, deletedCounter, handleMenuItemClick }) => (
    <ul className="nav flex-column">
      <li className="sidebar-section">
        <Text size="sm" fw={500} mt="md">
          WhereCanWeDance
        </Text>
      </li>
      {user.superadmin && (
        <>
          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Accordion multiple>
              <Accordion.Item value="manage">
                <Accordion.Control>
                  <Group gap="apart" align="center">
                    <span>Manage</span>
                    <span className="badge">{pendingCounter}</span>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <NavLink component={Link} href="/status" label={`Pending ${pendingCounter}`} />
                  <NavLink component={Link} href="/new_dance_style" label="Create Dance Style" />
                  <NavLink component={Link} href="/users" label="Manage Users" />
                  <NavLink component={Link} href="/roles" label="Manage Posts" />
                  <NavLink component={Link} href="/roles" label="Manage Roles" />
                  <NavLink component={Link} href="/organizers" label="Manage Organizers" />
                  <NavLink component={Link} href="/manage_subscriptions" label="Manage Subscriptions" />
                  <NavLink component={Link} href="/videos" label="Manage Videos" />
                  <NavLink component={Link} href="/recurring_events" label="Manage Recurring Events" />
                  <NavLink component={Link} href="/admin_star_purchases" label="Star Purchases" />
                  <NavLink component={Link} href="/admin_user_artist_dashboard" label="Claimed Artist Dashboard" />
                  <NavLink component={Link} href="/discarded_items" label={`Deleted Items ${deletedCounter}`} />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </li>
        </>
      )}

      {user && (
        <>
          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <NavLink
              component="button"
              onClick={() => handleMenuItemClick('account')}
              label="Manage Your Account"
              leftSection={<IconUserFilled size={16} />}
            />
            <NavLink
              component="button"
              leftSection={<IconSettings size={16} />}
              onClick={() => handleMenuItemClick('settings')}
              label="Settings"
            />
          </li>

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500}>
              Dance Life
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              href={`/dance_journal/${user.id}`}
              leftSection={<IconNotebook size={16} />}
              label="Your Dance Journal"
            />
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconWallet size={16} />}
              href={`/dance_wallet/${user.id}`}
              label="Your Dance Wallet"
            />
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconSchool size={16} />}
              href={`/your_private_lessons/${user.id}`}
              label="Your Private Lessons"
            />
          </li>

          {user.hasOrganizer && (
            <>
              <li className="sidebar-divider" />
              <li className="sidebar-section">
                <Text size="sm" fw={500}>
                  Organizations
                </Text>
                <NavLink
                  component={Link}
                  c="#f2f2f2"
                  leftSection={<IconBriefcase size={16} />}
                  href={`/manage_organizations/${user.id}`}
                  label="Your Organizations"
                />
              </li>
            </>
          )}

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500} className="text-amber-200">
              Your Events
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconBookmarks size={16} />}
              href={`/bookmarked_events/${user.id}`}
              label="Your Bookmarked Events"
            />
            <NavLink
              component={Link}
              leftSection={<IconCalendarStats size={16} />}
              c="#f2f2f2"
              href={`/managed_events/${user.id}`}
              label="Events You Manage"
            />
          </li>

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500}>
              Your Artists
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconList size={16} />}
              href={`/your_booking_portals/${user.id}`}
              label="Your Artist Booking Portal(s)"
            />
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconUsers size={16} />}
              href={`/managed_artists/${user.id}`}
              label="Artists You Manage"
            />
            <NavLink
              component="button"
              c="#f2f2f2"
              leftSection={<IconReceiptDollar size={16} />}
              onClick={() => {
                handleMenuItemClick('privateLessons');
                // Optionally set active tab here
              }}
              label="Private Lesson Management"
            />
          </li>

          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <Text size="sm" fw={500}>
              Your Purchases
            </Text>
            <NavLink
              component={Link}
              c="#f2f2f2"
              leftSection={<IconReceipt size={16} />}
              href={`/star_purchases/${user.id}`}
              label="View Star Purchases"
            />
          </li>
        </>
      )}

      {!user && (
        <>
          <li className="sidebar-divider" />
          <li className="sidebar-section">
            <NavLink component={Link} leftSection={<IconLogin2 size={16} />} href="/login" label="Log in" />
            <NavLink component={Link} leftSection={<IconUserPLus size={16} />} href="/signup" label="Sign up" />
          </li>
        </>
      )}
    </ul>
  );

  return (
    <MantineProvider theme={theme} withGlobalStyles withNormalizeCSS>
      <div style={{ display: 'flex', position: 'relative' }}>
        {/* Mobile Toggle Button - Fixed Position */}
        {isMobile && (
          <ActionIcon
            variant="filled"
            color="orange"
            size="xl"
            radius="md"
            style={{
              position: 'fixed',
              top: '100px',
              left: '15px',
              zIndex: 1000,
            }}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <IconMenu2 size={28} />
          </ActionIcon>
        )}

        {/* Desktop Sidebar */}
        {!isMobile && (
          <div className="sidebar-content" style={{ width: '250px', minHeight: '100vh', padding: '1rem' }}>
            <SidebarContent
              user={user}
              pendingCounter={pendingCounter}
              deletedCounter={deletedCounter}
              handleMenuItemClick={handleMenuItemClick}
            />
          </div>
        )}

        {/* Mobile Sidebar Drawer */}
        {isMobile && (
          <Drawer
            opened={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            size="sm"
            position="left"
            zIndex={1001}
            styles={{
              header: { backgroundColor: '#171717' },
              body: {
                backgroundColor: '#171717',
                padding: '0rem 0 0 0',
                minHeight: '100%',
                height: 'auto',
              },
            }}
          >
            <div className="sidebar-content" style={{ height: '100%', padding: '1rem' }}>
              <SidebarContent
                user={user}
                pendingCounter={pendingCounter}
                deletedCounter={deletedCounter}
                handleMenuItemClick={handleMenuItemClick}
              />
            </div>
          </Drawer>
        )}

        {/* Main Content */}
        <div style={{ flex: 1, paddingTop: '1.5rem' }}>
          <RightContentSection
            section={activeSection}
            user={user}
            claimedArtist={user.claimed_artist}
            privateLessonTypes={private_lesson_types}
            privateLessonPages={private_lesson_pages}
            privateLessonBookings={private_lesson_bookings}
            initialTab={activeTab}
            available_roles={available_roles}
          />
        </div>
      </div>
    </MantineProvider>
  );
};

export default UserSettings;
